import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { StatusPill, ContactFlag, ArchivedBadge, UndeliveredFlag } from "../../components/founder/ui";
import BusinessActions from "../../components/founder/BusinessActions";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Plus, Search, AlertTriangle } from "lucide-react";

export default function Businesses() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [view, setView] = useState("active"); // active | archived
  const [dispFilter, setDispFilter] = useState("");
  const [fcFilter, setFcFilter] = useState("");
  const [meta, setMeta] = useState({ statuses: [], dispositions: [], future_contact: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [dupes, setDupes] = useState([]);
  const navigate = useNavigate();

  const load = () => {
    const params = { archived: view === "archived" };
    if (filter) params.status = filter;
    if (q) params.q = q;
    if (view === "archived" && dispFilter) params.disposition = dispFilter;
    if (view === "archived" && fcFilter) params.future_contact_status = fcFilter;
    api.get("/businesses", { params }).then((r) => setItems(r.data));
  };
  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q, filter, view, dispFilter, fcFilter]);

  // duplicate detection while creating a new business
  useEffect(() => {
    const name = form.business_name || "";
    if (!open || name.trim().length < 2) { setDupes([]); return; }
    const t = setTimeout(() => {
      api.get("/business-duplicates", { params: { name, phone: form.phone, email: form.email } })
        .then((r) => setDupes(r.data)).catch(() => setDupes([]));
    }, 350);
    return () => clearTimeout(t);
  }, [form.business_name, form.phone, form.email, open]);

  const create = async () => {
    if (!form.business_name) { toast.error("Business name required"); return; }
    try {
      const { data } = await api.post("/businesses", form);
      setOpen(false); setForm({}); setDupes([]);
      navigate(`/app/businesses/${data.id}`);
    } catch { toast.error("Could not create"); }
  };

  const fcLabel = (v) => (meta.future_contact || []).find((f) => f.value === v)?.label;

  return (
    <div className="p-5 md:p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h1 className="font-serif text-3xl md:text-4xl text-lr-charcoal">Businesses</h1>
        <Button onClick={() => setOpen(true)} data-testid="new-business-btn" className="bg-lr-olive hover:bg-[#3d4230] text-lr-cream rounded-[4px]">
          <Plus className="h-4 w-4 mr-1" /> New Business
        </Button>
      </div>

      <div className="inline-flex rounded-[4px] border hairline overflow-hidden mb-5">
        {[["active", "Active"], ["archived", "Archived / History"]].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
            className={`px-4 py-2 text-sm transition-colors ${view === v ? "bg-lr-olive text-lr-cream" : "bg-transparent text-lr-charcoal/80 hover:bg-lr-olive/10"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-lr-leather" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, owner, trade…"
            data-testid="business-search" className="pl-9 bg-white/60" />
        </div>
        {view === "active" ? (
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56 bg-white/60" data-testid="status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <>
            <Select value={dispFilter || "all"} onValueChange={(v) => setDispFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-56 bg-white/60" data-testid="disposition-filter"><SelectValue placeholder="All dispositions" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">All dispositions</SelectItem>
                {(meta.dispositions || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fcFilter || "all"} onValueChange={(v) => setFcFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-56 bg-white/60" data-testid="future-contact-filter"><SelectValue placeholder="All contact statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All contact statuses</SelectItem>
                {(meta.future_contact || []).map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {items.length === 0 ? (
        <div className="lr-card p-8 text-center text-lr-leather">
          {view === "archived" ? "No archived businesses match." : "No businesses match. Add one to get started."}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {items.map((b) => (
            <div key={b.id} onClick={() => navigate(`/app/businesses/${b.id}`)} data-testid={`business-card-${b.id}`}
              role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && navigate(`/app/businesses/${b.id}`)}
              className="lr-card p-5 text-left hover:border-lr-olive/50 transition-colors cursor-pointer">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-serif text-lg text-lr-charcoal leading-tight">{b.business_name}</h3>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {b.is_demo && <span className="text-[10px] tracking-wide px-1.5 py-0.5 rounded-[3px] bg-lr-leather/15 text-lr-leather">DEMO</span>}
                  {b.paid
                    ? <span className="text-[10px] tracking-wide px-1.5 py-0.5 rounded-[3px] bg-lr-olive/15 text-lr-olive" data-testid={`paid-badge-${b.id}`}>PAID</span>
                    : <span className="text-[10px] tracking-wide px-1.5 py-0.5 rounded-[3px] bg-lr-clay/10 text-lr-clay/80" data-testid={`unpaid-badge-${b.id}`}>UNPAID</span>}
                  <BusinessActions business={b} onChange={load} meta={meta} />
                </div>
              </div>
              <p className="text-sm text-lr-leather mt-1">{b.owner_name || "Not recorded"}{b.primary_trade ? ` · ${b.primary_trade}` : ""}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusPill status={b.status} />
                {b.archived && <ArchivedBadge />}
                <UndeliveredFlag status={b.status} />
                <ContactFlag status={b.future_contact_status} />
              </div>
              {view === "archived" && b.disposition && <p className="text-xs text-lr-leather mt-2">{b.disposition}{b.archived_at ? ` · ${b.archived_at.slice(0, 10)}` : ""}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-lr-card max-h-[85vh] overflow-y-auto lr-scroll">
          <DialogHeader><DialogTitle className="font-serif">New Business</DialogTitle></DialogHeader>
          {dupes.length > 0 && (
            <div className="rounded-[4px] border border-lr-clay/40 bg-lr-clay/10 p-3 text-sm" data-testid="duplicate-warning">
              <div className="flex items-center gap-2 text-lr-clay font-medium mb-2"><AlertTriangle className="h-4 w-4" /> This business may already exist in Long Range</div>
              <ul className="space-y-1.5">
                {dupes.map((d) => (
                  <li key={d.id}>
                    <button type="button" onClick={() => { setOpen(false); navigate(`/app/businesses/${d.id}`); }}
                      className="text-left text-lr-charcoal hover:text-lr-olive hover:underline" data-testid={`dupe-${d.id}`}>
                      {d.business_name}
                      {d.archived ? ` · Archived${d.archived_at ? " " + d.archived_at.slice(0, 10) : ""}` : ` · ${d.status}`}
                      {d.disposition ? ` · ${d.disposition}` : ""}
                      {d.future_contact_status === "do_not_contact" ? " · Do not contact" : ""}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="space-y-4 py-2">
            {[["business_name", "Business name"], ["owner_name", "Owner name"], ["primary_trade", "Primary trade"], ["location", "Location"], ["phone", "Phone"], ["email", "Email"], ["source", "How Long Range found them"]].map(([k, l]) => (
              <div key={k}>
                <Label className="text-lr-charcoal">{l}</Label>
                <Input className="mt-1 bg-white/60" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`new-biz-${k}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-[4px]">Cancel</Button>
            <Button onClick={create} data-testid="create-business-submit" className="bg-lr-olive hover:bg-[#3d4230] text-lr-cream rounded-[4px]">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
